import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint, PdfBrush, PdfColor } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/label';
import '../../pdf/draw/shapes';
import '../../pdf/draw/cut';

/**
 * Duha (Rainbow) - Rainbow arcs with cloud images
 *
 * Layout: 5 portrait pages, 2 puzzles per page.
 * Each puzzle: rainbow arcs (6 colors) with colored dots at each arc,
 * flanked by two clouds containing images.
 *
 * Migrated from C# EduFrame.Img10.Generators.Duha
 */
export class Duha implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const pointSize = PdfUnit.FromMillimeters(5);

    const clouds = GUtil.randomBrushes(opts.data.random, [
      [
        '#a3c2e0',
        '#96bbde',
        '#90b7dd',
        '#89b3dc',
        '#83afdb',
        '#7cabda',
        '#76a7d9',
        '#6fa3d8',
        '#6ba1da',
        '#669fdb',
      ],
      [
        '#d5cbe5',
        '#cbc4e2',
        '#c6c1e1',
        '#c1bddf',
        '#bcbade',
        '#b7b6dc',
        '#b5b4dc',
        '#b2b2db',
        '#b0b0da',
        '#adaed9',
      ],
    ]);

    const colors: PdfBrush[] = [
      new PdfBrush(PdfColor.fromRgb(255, 0, 0)),
      new PdfBrush(PdfColor.fromRgb(255, 165, 0)),
      new PdfBrush(PdfColor.fromRgb(255, 255, 0)),
      new PdfBrush(PdfColor.fromRgb(0, 128, 0)),
      new PdfBrush(PdfColor.fromRgb(0, 0, 255)),
      new PdfBrush(PdfColor.fromRgb(238, 130, 238)),
    ];

    const puzzlesPerPage = 2;
    const pagesNeeded = Math.ceil(opts.list.length / puzzlesPerPage);
    const pages: ReturnType<typeof pdf.addPage>[] = [];
    for (let i = 0; i < pagesNeeded; i++) pages.push(pdf.addPage());

    for (let i = 0; i < opts.list.length; i++) {
      const pageIndex = Math.floor(i / 2);
      if (pageIndex >= pages.length) break;
      const page = pages[pageIndex];

      const rect = new PdfRect(
        margin,
        margin + ((i % 2) * PdfSize.A4.height) / 2,
        PdfSize.A4.width - 2 * margin,
        PdfSize.A4.height / 2 - 2 * margin
      );
      const margin2 = PdfUnit.FromMillimeters(10);

      const size = rect.width / 2 - margin2;
      const ray = (size * 0.8) / colors.length;
      const {centerX} = rect;
      const centerY = rect.y + (rect.height - size) / 2 + size;

      const r1 = new PdfRect(
        centerX - (size - ray * -0.5),
        centerY - rect.height * 0.25,
        ray * (colors.length + 1),
        rect.height * 0.3
      );
      const r2 = new PdfRect(centerX + (centerX - r1.right), r1.y, r1.width, r1.height);

      const r1l = new PdfRect(
        r1.x + r1.width * 0.35,
        r1.y + r1.height * 0.1,
        r1.width * 0.4,
        r1.height * 0.85
      );
      const r2l = new PdfRect(
        r2.x + r2.width * 0.2,
        r2.y + r2.height * 0.4,
        r2.width * 0.65,
        r2.height * 0.55
      );

      page.cutCross(new PdfPoint(0, PdfSize.A4.height / 2), true, false);

      // Draw rainbow arcs
      for (let j = 0; j < colors.length; j++) {
        const s = size - ray * j;
        const s1 = size - ray * (j + 0.5);
        const r = new PdfRect(centerX - s, centerY - s, s * 2, s * 2);
        const rr = new PdfRect(
          centerX - pointSize / 2,
          centerY - s1 - pointSize / 2,
          pointSize,
          pointSize
        );

        page.drawPie(GUtil.basePen, null, r, 180, 360);
        page.drawEllipse(null, colors[j], rr);
      }

      // Draw clouds with images
      page.drawCloud(r1, clouds[i % clouds.length], null);
      page.drawCloud(r2, clouds[i % clouds.length], null);

      page.drawLabel(opts.list[i].left, { rect: r1l });
      page.drawLabel(opts.list[i].right, { rect: r2l });
    }
  }
}
